import { useEffect, useMemo, useState } from "react";
import * as userService from "../services/userService";
import CreateUserModal from "../components/CreateUserModal";

function etiquetaRol(roles: string[]) {
  if (roles.includes("SYSTEM_ADMIN")) return "Administrador";
  if (roles.includes("EVENT_ADMIN")) return "Editor";
  return "Lector";
}

function claseBadgeRol(label: string) {
  if (label === "Administrador")
    return "bg-primary/10 text-primary border border-primary/20";
  if (label === "Editor")
    return "bg-purple-500/10 text-purple-400 border border-purple-500/20";
  return "bg-slate-500/10 text-slate-400 border border-slate-500/20";
}

function clasePuntoEstado(activo: boolean) {
  return activo ? "bg-emerald-500" : "bg-slate-500";
}

function textoEstado(activo: boolean) {
  return activo ? "Activo" : "Inactivo";
}

function UsersPage() {
  const [users, setUsers] = useState<userService.UsuarioAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [openCreate, setOpenCreate] = useState(false);

  async function cargarUsuarios() {
    setLoading(true);
    setErrorMsg(null);
    try {
      const data = await userService.listarUsuarios();
      setUsers(data);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    cargarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  async function toggleActivo(u: userService.UsuarioAdmin) {
    try {
      const updated = await userService.cambiarEstadoUsuario(u.id, !u.activo);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
    } catch (e) {
      alert(e instanceof Error ? e.message : "Error actualizando estado");
    }
  }

  async function onCreatedUser(newUser: userService.UsuarioAdmin) {
    setOpenCreate(false);
    setUsers((prev) => [newUser, ...prev]);
  }

  return (
    <>
      <div className="p-4 sm:p-6 lg:p-12 max-w-7xl mx-auto w-full flex flex-col gap-8">
        {/* Título + acción */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 dark:text-white mb-2">
              Gestión de usuarios
            </h2>
            <p className="text-slate-500 dark:text-slate-400">
              Administra accesos, roles y permisos del equipo.
            </p>
          </div>

          <button
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gold-accent hover:bg-[#c5a028] text-surface-darker font-bold py-2.5 px-5 rounded-lg transition-all shadow-lg shadow-gold-accent/20 active:scale-95"
            type="button"
            onClick={() => setOpenCreate(true)}
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            <span>Crear usuario</span>
          </button>
        </div>

        {/* Barra de herramientas */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white dark:bg-surface-dark p-4 rounded-xl border border-slate-200 dark:border-white/5 shadow-sm">
          <div className="relative w-full md:w-96">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
              search
            </span>
            <input
              className="w-full bg-slate-50 dark:bg-background-dark border border-slate-200 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
              placeholder="Buscar por nombre o correo..."
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-background-dark border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
              type="button"
              onClick={cargarUsuarios}
            >
              <span className="material-symbols-outlined text-[18px]">
                refresh
              </span>
              Actualizar
            </button>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-white/5 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02]">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Nombre
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Rol
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Correo
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 text-right">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                {loading && (
                  <tr>
                    <td className="px-6 py-6 text-sm text-slate-500 dark:text-slate-400" colSpan={5}>
                      Cargando...
                    </td>
                  </tr>
                )}

                {!loading && errorMsg && (
                  <tr>
                    <td className="px-6 py-6 text-sm text-red-500" colSpan={5}>
                      {errorMsg}
                    </td>
                  </tr>
                )}

                {!loading &&
                  !errorMsg &&
                  filteredUsers.map((u) => {
                    const rol = etiquetaRol(u.roles);
                    return (
                      <tr
                        key={u.id}
                        className="group hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-white">
                              {u.nombre}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {u.telefono ?? ""}
                            </p>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${claseBadgeRol(
                              rol
                            )}`}
                          >
                            {rol}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-slate-600 dark:text-slate-300">
                            {u.email}
                          </p>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span
                              className={`size-2 rounded-full ${clasePuntoEstado(
                                u.activo
                              )}`}
                            ></span>
                            <span className="text-sm text-slate-600 dark:text-slate-300">
                              {textoEstado(u.activo)}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                              title="Activar/Desactivar"
                              type="button"
                              onClick={() => toggleActivo(u)}
                            >
                              <span className="material-symbols-outlined text-[18px]">
                                {u.activo ? "block" : "check_circle"}
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Footer tabla */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02]">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Mostrando{" "}
              <span className="font-medium text-slate-900 dark:text-white">
                {filteredUsers.length}
              </span>{" "}
              resultado(s)
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <CreateUserModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={onCreatedUser}
      />
    </>
  );
}

export default UsersPage;
